import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Heading,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi";
import CodesetSelect from "../../CodesetSelect";
import UuidPreview from "../../UuidPreview";
import CurrencyAmountInput from "../../CurrencyAmountInput";
import RateAmountInput from "../../RateAmountInput";
import { createEmptyPremium, createEmptyInstalment } from "../../utils/placementDefaults";

const InstalmentEditor = ({ instalment, instalmentIndex, onUpdate, onRemove }) => {
  const updateField = (field, value) => {
    onUpdate(instalmentIndex, field, value);
  };

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={4}
      mb={4}
      borderColor="gray.300">
      <UuidPreview uuid={instalment.id} />

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={4}>
        <FormControl>
          <FormLabel>Sequence#</FormLabel>
          <Input
            type="number"
            value={instalment.sequenceNbr}
            onChange={(e) => updateField("sequenceNbr", parseInt(e.target.value) || 0)}
          />
        </FormControl>

        <CurrencyAmountInput
          label="Amount"
          value={instalment.amount}
          onChange={(value) => updateField("amount", value)}
        />

        <RateAmountInput
          label="Instalment%"
          value={instalment.percentage}
          onChange={(value) => updateField("percentage", value)}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={4}>
        <RateAmountInput
          label="Rate%"
          value={instalment.rate}
          onChange={(value) => updateField("rate", value)}
        />
        <RateAmountInput
          label="Min Rate%"
          value={instalment.minimumRate}
          onChange={(value) => updateField("minimumRate", value)}
        />
        <RateAmountInput
          label="Max Rate%"
          value={instalment.maximumRate}
          onChange={(value) => updateField("maximumRate", value)}
        />

        <CodesetSelect
          label="Income Amount Basis"
          codesetCode="A90"
          value={
            instalment.incomeAmountBasis
              ? {
                  code: instalment.incomeAmountBasis,
                  description: "",
                }
              : null
          }
          onChange={(value) => updateField("incomeAmountBasis", value.code)}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <FormControl>
          <FormLabel>Terms of Trade Period Duration</FormLabel>
          <Input
            type="number"
            value={instalment.termsOfTradePeriodDuration}
            onChange={(e) =>
              updateField("termsOfTradePeriodDuration", parseInt(e.target.value) || 0)
            }
          />
        </FormControl>

        <FormControl>
          <FormLabel>Terms of Trade Period Basis</FormLabel>
          <Input
            value={instalment.termsOfTradePeriodBasis}
            onChange={(e) => updateField("termsOfTradePeriodBasis", e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Description</FormLabel>
          <Input
            value={instalment.description}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <FormControl>
          <FormLabel>Due Date/Time</FormLabel>
          <Input
            type="datetime-local"
            value={instalment.dueDateTime?.value || ""}
            onChange={(e) =>
              updateField("dueDateTime", {
                ...instalment.dueDateTime,
                value: e.target.value,
              })
            }
          />
        </FormControl>

        <FormControl>
          <FormLabel>Payable By Date/Time</FormLabel>
          <Input
            type="datetime-local"
            value={instalment.payableByDateTime?.value || ""}
            onChange={(e) =>
              updateField("payableByDateTime", {
                ...instalment.payableByDateTime,
                value: e.target.value,
              })
            }
          />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end">
        <IconButton
          size="md"
          icon={<HiOutlineTrash />}
          aria-label="Delete Instalment"
          onClick={() => onRemove(instalmentIndex)}
        />
      </HStack>
    </Box>
  );
};

const PremiumEditor = ({ premium, premiumIndex, onUpdate, onRemove }) => {
  const updateField = (field, value) => {
    onUpdate(premiumIndex, field, value);
  };

  const updateInstalment = (instalmentIndex, field, value) => {
    const updatedInstalments = [...(premium.instalments || [])];
    updatedInstalments[instalmentIndex] = {
      ...updatedInstalments[instalmentIndex],
      [field]: value,
    };
    updateField("instalments", updatedInstalments);
  };

  const addInstalment = () => {
    const updatedInstalments = [...(premium.instalments || []), createEmptyInstalment()];
    updateField("instalments", updatedInstalments);
  };

  const removeInstalment = (instalmentIndex) => {
    const updatedInstalments = [...premium.instalments];
    updatedInstalments.splice(instalmentIndex, 1);
    updateField("instalments", updatedInstalments);
  };

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={4}
      mb={6}
      borderColor="gray.500">
      <UuidPreview uuid={premium.id} />

      <FormControl mb={4}>
        <FormLabel>Description</FormLabel>
        <Input
          value={premium.description || ""}
          onChange={(e) => updateField("description", e.target.value)}
        />
      </FormControl>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <CodesetSelect
          label="Type"
          codesetCode="A85"
          value={
            premium.type
              ? {
                  code: premium.type,
                  description: "",
                }
              : null
          }
          onChange={(value) => updateField("type", value.code)}
        />

        <CurrencyAmountInput
          label="Amount"
          value={premium.amount}
          onChange={(value) => updateField("amount", value)}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={4}>
        <RateAmountInput
          label="Rate"
          value={premium.rate}
          onChange={(value) => updateField("rate", value)}
        />

        <RateAmountInput
          label="Minimum Rate"
          value={premium.minimumRate}
          onChange={(value) => updateField("minimumRate", value)}
        />

        <RateAmountInput
          label="Maximum Rate"
          value={premium.maximumRate}
          onChange={(value) => updateField("maximumRate", value)}
        />

        <CodesetSelect
          label="Income Amount Basis"
          codesetCode="A90"
          value={
            premium.incomeAmountBasis
              ? {
                  code: premium.incomeAmountBasis,
                  description: "",
                }
              : null
          }
          onChange={(value) => updateField("incomeAmountBasis", value.code)}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <FormControl>
          <FormLabel>Terms of Trade Period (Days)</FormLabel>
          <Input
            type="number"
            value={premium.termsOfTradePeriodDays}
            onChange={(e) =>
              updateField("termsOfTradePeriodDays", parseInt(e.target.value) || 0)
            }
          />
        </FormControl>

        <FormControl>
          <FormLabel>Number of Instalments</FormLabel>
          <Input
            type="number"
            value={premium.numberOfInstalments}
            onChange={(e) =>
              updateField("numberOfInstalments", parseInt(e.target.value) || 0)
            }
          />
        </FormControl>
      </SimpleGrid>

      <Box mb={4}>
        <HStack alignItems="center" mb={2}>
          <Heading size="sm">Instalments</Heading>
          <IconButton
            icon={<HiOutlinePlus />}
            size="sm"
            aria-label="Add Instalment"
            onClick={addInstalment}
          />
        </HStack>

        {(premium.instalments || []).map((instalment, instalmentIndex) => (
          <InstalmentEditor
            key={instalment.id}
            instalment={instalment}
            instalmentIndex={instalmentIndex}
            onUpdate={updateInstalment}
            onRemove={removeInstalment}
          />
        ))}
      </Box>

      <HStack justify="flex-end">
        <IconButton
          size="md"
          icon={<HiOutlineTrash />}
          aria-label="Delete Premium"
          onClick={() => onRemove(premiumIndex)}
        />
      </HStack>
    </Box>
  );
};

const PremiumsEditor = ({ premiums = [], onChange }) => {
  const updatePremium = (premiumIndex, field, value) => {
    const updated = [...premiums];
    updated[premiumIndex] = {
      ...updated[premiumIndex],
      [field]: value,
    };
    onChange(updated);
  };

  const addPremium = () => {
    onChange([...premiums, createEmptyPremium()]);
  };

  const removePremium = (premiumIndex) => {
    const updated = [...premiums];
    updated.splice(premiumIndex, 1);
    onChange(updated);
  };

  return (
    <Box>
      <HStack alignItems="center" mb={4}>
        <Heading size="md">Premiums</Heading>
        <IconButton
          icon={<HiOutlinePlus />}
          size="md"
          aria-label="Add Premium"
          onClick={addPremium}
        />
      </HStack>

      {premiums.map((premium, premiumIndex) => (
        <PremiumEditor
          key={premium.id}
          premium={premium}
          premiumIndex={premiumIndex}
          onUpdate={updatePremium}
          onRemove={removePremium}
        />
      ))}
    </Box>
  );
};

export default PremiumsEditor;
